import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Loader2, Save, X, Trash2 } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface MatchData {
  id: string;
  type: 'league' | 'global';
  leagueId?: string;
  score: any;
  winner: number;
  team1Player1Id?: string;
  team1Player2Id?: string | null;
  team2Player1Id?: string;
  team2Player2Id?: string | null;
  playedAt?: string | Date | null;
  // Optional player names so the live result preview can use real names
  // instead of the generic "Equipo 1 / Equipo 2" labels when available.
  team1Player1Name?: string | null;
  team1Player2Name?: string | null;
  team2Player1Name?: string | null;
  team2Player2Name?: string | null;
}

interface UserLeagueOption {
  id: string;
  name: string;
}

const GLOBAL_OPTION = "__global__";

interface EditPendingMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  match: MatchData | null;
  onSuccess?: () => void;
}

const COMMON_SCORES = [0, 1, 2, 3, 4, 5, 6, 7];
const QUICK_RESULTS: ReadonlyArray<{ team1: number; team2: number }> = [
  { team1: 6, team2: 0 },
  { team1: 6, team2: 2 },
  { team1: 6, team2: 4 },
  { team1: 7, team2: 5 },
  { team1: 7, team2: 6 },
];

function autoFillOther(picked: number, currentOther: number): number {
  if (currentOther !== 0) return currentOther;
  if (picked >= 0 && picked <= 4) return 6;
  if (picked === 5) return 7;
  if (picked === 7) return 5;
  return currentOther;
}

export function EditPendingMatchDialog({
  open,
  onOpenChange,
  match,
  onSuccess,
}: EditPendingMatchDialogProps) {
  const { toast } = useToast();
  const [sets, setSets] = useState<{ team1: number; team2: number }[]>([{ team1: 0, team2: 0 }]);
  const [playedAtDate, setPlayedAtDate] = useState<string>("");
  // The currently chosen destination context. `__global__` means the
  // match should live in global play; any other value is a leagueId.
  const [contextValue, setContextValue] = useState<string>(GLOBAL_OPTION);

  const todayStr = new Date().toISOString().slice(0, 10);

  // Editor's leagues — used to populate the destination selector. We
  // only fetch while the dialog is open so we don't add extra requests
  // to the page load.
  const { data: userLeagues = [] } = useQuery<UserLeagueOption[]>({
    queryKey: ["/api/leagues"],
    enabled: open,
  });

  useEffect(() => {
    if (match && open) {
      const matchSets = getScoreSets(match.score);
      if (matchSets.length > 0) {
        setSets(matchSets);
      } else {
        setSets([{ team1: 0, team2: 0 }]);
      }
      const seed = match.playedAt ? new Date(match.playedAt) : new Date();
      if (Number.isNaN(seed.getTime())) {
        setPlayedAtDate(todayStr);
      } else {
        // Format as local YYYY-MM-DD so the picker shows the same day as the match.
        const y = seed.getFullYear();
        const m = String(seed.getMonth() + 1).padStart(2, "0");
        const d = String(seed.getDate()).padStart(2, "0");
        setPlayedAtDate(`${y}-${m}-${d}`);
      }
      setContextValue(
        match.type === 'league' && match.leagueId ? match.leagueId : GLOBAL_OPTION,
      );
    }
  }, [match, open, todayStr]);

  const getScoreSets = (score: any) => {
    if (typeof score === 'string') {
      return score.split(',').map(s => {
        const [team1, team2] = s.trim().split('-').map(Number);
        return { team1: team1 || 0, team2: team2 || 0 };
      });
    }
    if (score?.sets && Array.isArray(score.sets)) {
      return score.sets.map((s: any) => ({
        team1: s.team1 || 0,
        team2: s.team2 || 0,
      }));
    }
    return [{ team1: 0, team2: 0 }];
  };

  const addSet = () => {
    setSets([...sets, { team1: 0, team2: 0 }]);
  };

  const removeSet = (index: number) => {
    if (sets.length > 1) {
      setSets(sets.filter((_, i) => i !== index));
    }
  };

  const updateSet = (index: number, team: 'team1' | 'team2', value: number) => {
    const newSets = [...sets];
    const other: 'team1' | 'team2' = team === 'team1' ? 'team2' : 'team1';
    newSets[index] = {
      ...newSets[index],
      [team]: value,
      [other]: autoFillOther(value, newSets[index][other]),
    };
    setSets(newSets);
  };

  const applyQuickResult = (index: number, team1: number, team2: number) => {
    const newSets = [...sets];
    newSets[index] = { team1, team2 };
    setSets(newSets);
  };

  // ---- Live result preview ----------------------------------------
  // Mirrors the "Editar marcador" sheet on mobile so the editor sees the
  // same immediate feedback (set tally + who's currently winning) instead
  // of just the plain "Ganador: …" line. Names are used when available.
  const allValid = sets.every(
    (s) =>
      Number.isFinite(s.team1) &&
      Number.isFinite(s.team2) &&
      s.team1 >= 0 &&
      s.team2 >= 0,
  );
  const team1Wins = allValid
    ? sets.filter((s) => s.team1 > s.team2).length
    : 0;
  const team2Wins = allValid
    ? sets.filter((s) => s.team2 > s.team1).length
    : 0;
  const setsWithWinner = allValid
    ? sets.filter((s) => s.team1 !== s.team2).length
    : 0;
  const allSetsResolved = allValid && setsWithWinner === sets.length;
  const winnerNum: 1 | 2 | null = !allValid
    ? null
    : team1Wins === team2Wins
      ? null
      : team1Wins > team2Wins
        ? 1
        : 2;

  const team1Names = useMemo(() => {
    if (!match) return "";
    return [match.team1Player1Name, match.team1Player2Name]
      .filter(Boolean)
      .join(" / ");
  }, [match]);
  const team2Names = useMemo(() => {
    if (!match) return "";
    return [match.team2Player1Name, match.team2Player2Name]
      .filter(Boolean)
      .join(" / ");
  }, [match]);

  const resultPreview = useMemo<{
    text: string;
    tone: "muted" | "team1" | "team2";
  }>(() => {
    if (!allValid) {
      return {
        text: "Ingresa los puntajes para ver el resultado",
        tone: "muted",
      };
    }
    if (setsWithWinner === 0) {
      return { text: "Sin definir aún", tone: "muted" };
    }
    if (team1Wins === team2Wins) {
      return { text: `Empate ${team1Wins}-${team2Wins}`, tone: "muted" };
    }
    const leadingTeam: 1 | 2 = team1Wins > team2Wins ? 1 : 2;
    const lead = Math.max(team1Wins, team2Wins);
    const trail = Math.min(team1Wins, team2Wins);
    const leaderName =
      leadingTeam === 1
        ? team1Names || "Equipo 1"
        : team2Names || "Equipo 2";
    return {
      text: `${leaderName} ${allSetsResolved ? "gana" : "va ganando"} ${lead}-${trail}`,
      tone: leadingTeam === 1 ? "team1" : "team2",
    };
  }, [
    allValid,
    setsWithWinner,
    team1Wins,
    team2Wins,
    team1Names,
    team2Names,
    allSetsResolved,
  ]);

  const determineWinner = (): 1 | 2 => {
    return team1Wins > team2Wins ? 1 : 2;
  };

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!match) throw new Error("No match to edit");
      if (sets.some((s: any) => s.team1 === s.team2)) {
        throw new Error("Cada set debe tener un ganador (sin empates).");
      }

      const winner = determineWinner();
      const scoreObj = { sets };

      // Combine the picked YYYY-MM-DD with the original played-at time so
      // we don't lose hour/minute precision when only the date is edited.
      let playedAtIso: string | undefined;
      if (playedAtDate) {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(playedAtDate);
        if (m) {
          const y = Number(m[1]);
          const mo = Number(m[2]);
          const da = Number(m[3]);
          const original = match.playedAt ? new Date(match.playedAt) : null;
          const out =
            original && !Number.isNaN(original.getTime())
              ? new Date(original)
              : new Date();
          out.setFullYear(y, mo - 1, da);
          if (!original || Number.isNaN(original.getTime())) {
            out.setHours(12, 0, 0, 0);
          }
          playedAtIso = out.toISOString();
        }
      }

      // Translate the selected option into the wire-format
      // `targetLeagueId`: `null` = move to global, string = league id.
      // For "no change" we still send the same value, which the backend
      // detects as a no-op move.
      const targetLeagueId: string | null =
        contextValue === GLOBAL_OPTION ? null : contextValue;

      if (match.type === 'global') {
        return apiRequest('PATCH', `/api/matches/global/${match.id}`, {
          team1Player2Id: match.team1Player2Id,
          team2Player1Id: match.team2Player1Id,
          team2Player2Id: match.team2Player2Id,
          winner,
          score: scoreObj,
          ...(playedAtIso ? { playedAt: playedAtIso } : {}),
          // Global PATCH only treats string as a move; null stays global.
          targetLeagueId,
        });
      } else {
        return apiRequest('PATCH', `/api/leagues/${match.leagueId}/matches/${match.id}/pending`, {
          team1Player1Id: match.team1Player1Id,
          team1Player2Id: match.team1Player2Id,
          team2Player1Id: match.team2Player1Id,
          team2Player2Id: match.team2Player2Id,
          winner,
          score: scoreObj,
          ...(playedAtIso ? { playedAt: playedAtIso } : {}),
          // League PATCH treats null as move-to-global, a different
          // string as move-to-other-league, same string as no-op.
          targetLeagueId,
        });
      }
    },
    onSuccess: async (response) => {
      if (response.ok) {
        toast({
          title: "Partido actualizado",
          description: "El partido ha sido editado. Los oponentes recibirán una notificación para aprobar.",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/matches/pending"] });
        queryClient.invalidateQueries({ queryKey: ["/api/matches/global/pending"] });
        queryClient.invalidateQueries({ queryKey: ["/api/profile/match-history"] });
        // Invalidate the league cache for both the source league (the
        // match may have left it) and the destination league (it may
        // have arrived there).
        if (match?.leagueId) {
          queryClient.invalidateQueries({ queryKey: ["/api/leagues", match.leagueId] });
        }
        if (contextValue !== GLOBAL_OPTION && contextValue !== match?.leagueId) {
          queryClient.invalidateQueries({ queryKey: ["/api/leagues", contextValue] });
        }
        onSuccess?.();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.message || "No se pudo actualizar el partido",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo actualizar el partido",
        variant: "destructive",
      });
    },
  });

  const handleEditClick = () => {
    editMutation.mutate();
    onOpenChange(false);
  };

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!match) throw new Error("No match to cancel");
      const url = match.type === 'global' 
        ? `/api/matches/global/${match.id}` 
        : `/api/matches/league/${match.id}`;
      return apiRequest('DELETE', url);
    },
    onSuccess: async (response) => {
      if (response.ok) {
        toast({
          title: "Partido cancelado",
          description: "El partido pendiente ha sido eliminado.",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/matches/pending"] });
        queryClient.invalidateQueries({ queryKey: ["/api/matches/global/pending"] });
        queryClient.invalidateQueries({ queryKey: ["/api/profile/match-history"] });
        if (match?.leagueId) {
          queryClient.invalidateQueries({ queryKey: ["/api/leagues", match.leagueId] });
        }
        onSuccess?.();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.message || "No se pudo cancelar el partido",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo cancelar el partido",
        variant: "destructive",
      });
    },
  });

  const handleCancelClick = () => {
    if (
      confirm(
        "¿Estás seguro de que quieres cancelar este partido? Esta acción no se puede deshacer.",
      )
    ) {
      cancelMutation.mutate();
      onOpenChange(false);
    }
  };

  if (!match) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Editar Partido Pendiente</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="select-edit-pending-context">Contexto del partido</Label>
            <Select value={contextValue} onValueChange={setContextValue}>
              <SelectTrigger id="select-edit-pending-context" data-testid="select-edit-pending-context">
                <SelectValue placeholder="Selecciona el contexto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={GLOBAL_OPTION} data-testid="option-context-global">
                  Partido Libre (Global)
                </SelectItem>
                {userLeagues.map((l) => (
                  <SelectItem key={l.id} value={l.id} data-testid={`option-context-league-${l.id}`}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Cambiar el contexto recalcula los puntos y reinicia la aprobación de los oponentes.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="input-edit-pending-played-at">Fecha del partido</Label>
            <Input
              id="input-edit-pending-played-at"
              type="date"
              value={playedAtDate}
              max={todayStr}
              onChange={(e) => setPlayedAtDate(e.target.value)}
              data-testid="input-edit-pending-played-at"
            />
          </div>
          <div className="space-y-3">
            <Label>Marcador por Sets</Label>
            {sets.map((set, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs text-muted-foreground">Set {index + 1}</Label>
                  <div className="flex flex-wrap items-center gap-1">
                    {QUICK_RESULTS.map((r) => {
                      const active = set.team1 === r.team1 && set.team2 === r.team2;
                      return (
                        <Button
                          key={`${r.team1}-${r.team2}`}
                          type="button"
                          size="sm"
                          variant={active ? "default" : "outline"}
                          className="h-7 px-2 text-xs"
                          onClick={() => applyQuickResult(index, r.team1, r.team2)}
                          data-testid={`button-edit-pending-quick-${index}-${r.team1}-${r.team2}`}
                        >
                          {r.team1}-{r.team2}
                        </Button>
                      );
                    })}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => applyQuickResult(index, set.team2, set.team1)}
                      data-testid={`button-edit-pending-invert-${index}`}
                    >
                      Invertir
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 justify-center">
                    <div className="grid grid-cols-4 gap-1">
                      {COMMON_SCORES.map((score) => (
                        <Button
                          key={score}
                          type="button"
                          size="sm"
                          variant={set.team1 === score ? "default" : "outline"}
                          className="h-8 w-8 p-0 text-xs"
                          onClick={() => updateSet(index, 'team1', score)}
                        >
                          {score}
                        </Button>
                      ))}
                    </div>
                    <span className="text-muted-foreground">-</span>
                    <div className="grid grid-cols-4 gap-1">
                      {COMMON_SCORES.map((score) => (
                        <Button
                          key={score}
                          type="button"
                          size="sm"
                          variant={set.team2 === score ? "default" : "outline"}
                          className="h-8 w-8 p-0 text-xs"
                          onClick={() => updateSet(index, 'team2', score)}
                        >
                          {score}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {sets.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => removeSet(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {sets.length < 5 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSet}
                className="w-full"
              >
                + Añadir Set
              </Button>
            )}
          </div>
          
          <div className="space-y-1 text-center">
            <p
              className={
                "text-sm font-medium " +
                (resultPreview.tone === "team1"
                  ? "text-primary"
                  : resultPreview.tone === "team2"
                    ? "text-destructive"
                    : "text-muted-foreground")
              }
              data-testid="text-edit-pending-result-preview"
            >
              {resultPreview.text}
            </p>
            {allValid && team1Wins === team2Wins && setsWithWinner > 0 ? (
              <p
                className="text-xs text-muted-foreground"
                data-testid="text-edit-pending-winner-hint"
              >
                Empate en sets — el ganador se decide por juegos totales.
              </p>
            ) : null}
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="destructive"
            onClick={handleCancelClick}
            disabled={editMutation.isPending || cancelMutation.isPending}
            className="sm:mr-auto"
          >
            {cancelMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Cancelar Partido
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={editMutation.isPending || cancelMutation.isPending}
            >
              Cerrar
            </Button>
            <Button
              onClick={handleEditClick}
              disabled={editMutation.isPending || cancelMutation.isPending}
              data-testid="button-save-edit-match"
            >
              {editMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
